import { useSyncExternalStore } from 'react';

export type UiReleaseMode = 'legacy' | 'stage1' | 'stage2' | 'stage3';
export type UiReleaseSurface =
  | 'landing-template'
  | 'landing-bespoke'
  | 'homepage'
  | 'pricing'
  | 'youtube'
  | 'dashboard';

export const UI_RELEASE_STORAGE_KEY = 'tc_ui_release_mode';
export const UI_RELEASE_QUERY_PARAM = 'ui';

export const UI_RELEASE_STAGES = [
  {
    mode: 'legacy',
    label: 'Legacy',
    description: 'Existing interface on every switched surface.',
  },
  {
    mode: 'stage1',
    label: 'Stage 1 — low risk',
    description: 'Redesigned template marketing pages only.',
  },
  {
    mode: 'stage2',
    label: 'Stage 2 — medium risk',
    description: 'Stage 1 plus bespoke landing pages and homepage.',
  },
  {
    mode: 'stage3',
    label: 'Stage 3 — full release',
    description: 'Stage 2 plus pricing, YouTube sales page, and dashboard overview.',
  },
] as const satisfies ReadonlyArray<{
  mode: UiReleaseMode;
  label: string;
  description: string;
}>;

export const UI_RELEASE_SWITCHED_SURFACES = [
  'Stage 1: template marketing landing pages',
  'Stage 2: bespoke landing pages and homepage',
  'Stage 3: pricing, YouTube sales page, and dashboard overview',
] as const;

/**
 * These workflows use one hardened implementation in both modes. There are no
 * separate, functionally equivalent legacy route components to select here.
 */
export const UI_RELEASE_SHARED_SURFACES = [
  'Login, signup and account recovery',
  'Campaign creation and project settings',
  'Checkout, payment, credits and billing',
] as const;

const UI_RELEASE_CHANGE_EVENT = 'tc-ui-release-change';
const DEFAULT_UI_RELEASE_MODE: UiReleaseMode = 'legacy';

const UI_RELEASE_RANK: Record<UiReleaseMode, number> = {
  legacy: 0,
  stage1: 1,
  stage2: 2,
  stage3: 3,
};

const SURFACE_MINIMUM_STAGE: Record<UiReleaseSurface, UiReleaseMode> = {
  'landing-template': 'stage1',
  'landing-bespoke': 'stage2',
  homepage: 'stage2',
  pricing: 'stage3',
  youtube: 'stage3',
  dashboard: 'stage3',
};

interface StorageReader {
  getItem(key: string): string | null;
}

interface StorageWriter extends StorageReader {
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ResolveUiReleaseModeOptions {
  isDev?: boolean;
  search?: string;
  storage?: StorageReader | null;
}

export interface GetUiReleaseModeOptions extends ResolveUiReleaseModeOptions {
  configuredValue?: unknown;
}

export interface SetLocalUiReleaseModeOptions {
  isDev?: boolean;
  storage?: StorageWriter | null;
  eventTarget?: EventTarget | null;
}

const parseUiReleaseMode = (value: unknown): UiReleaseMode | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'redesign') return 'stage3';
  return normalized === 'legacy'
    || normalized === 'stage1'
    || normalized === 'stage2'
    || normalized === 'stage3'
    ? normalized
    : null;
};

const browserStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const browserSearch = (): string => (
  typeof window === 'undefined' ? '' : window.location.search
);

/**
 * Resolves the release mode without reading global browser state.
 * Production always ignores query-string and local-storage overrides.
 */
export const resolveUiReleaseMode = (
  configuredValue: unknown,
  {
    isDev = false,
    search = '',
    storage = null,
  }: ResolveUiReleaseModeOptions = {},
): UiReleaseMode => {
  const configuredMode = parseUiReleaseMode(configuredValue) ?? DEFAULT_UI_RELEASE_MODE;

  if (!isDev) return configuredMode;

  let queryMode: UiReleaseMode | null = null;
  try {
    queryMode = parseUiReleaseMode(new URLSearchParams(search).get(UI_RELEASE_QUERY_PARAM));
  } catch {
    queryMode = null;
  }
  if (queryMode) return queryMode;

  try {
    const storedMode = parseUiReleaseMode(storage?.getItem(UI_RELEASE_STORAGE_KEY));
    if (storedMode) return storedMode;
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  return configuredMode;
};

/** Reads the current build setting and applies development-only overrides. */
export const getUiReleaseMode = ({
  configuredValue = import.meta.env.VITE_UI_RELEASE,
  isDev = import.meta.env.DEV,
  search = browserSearch(),
  storage = browserStorage(),
}: GetUiReleaseModeOptions = {}): UiReleaseMode => resolveUiReleaseMode(configuredValue, {
  isDev,
  search,
  storage,
});

/**
 * Sets or clears the local development override. Returns false in production
 * and never mutates storage there.
 */
export const setLocalUiReleaseMode = (
  mode: UiReleaseMode | null,
  {
    isDev = import.meta.env.DEV,
    storage = browserStorage(),
    eventTarget = typeof window === 'undefined' ? null : window,
  }: SetLocalUiReleaseModeOptions = {},
): boolean => {
  if (!isDev || !storage) return false;

  try {
    if (mode === null) {
      storage.removeItem(UI_RELEASE_STORAGE_KEY);
    } else {
      storage.setItem(UI_RELEASE_STORAGE_KEY, mode);
    }
  } catch {
    return false;
  }

  eventTarget?.dispatchEvent(new Event(UI_RELEASE_CHANGE_EVENT));
  return true;
};

const subscribeToUiReleaseMode = (onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  window.addEventListener(UI_RELEASE_CHANGE_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);
  window.addEventListener('popstate', onStoreChange);

  return () => {
    window.removeEventListener(UI_RELEASE_CHANGE_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener('popstate', onStoreChange);
  };
};

/** Reactive release mode for route selectors and the local control page. */
export const useUiReleaseMode = (): UiReleaseMode => useSyncExternalStore(
  subscribeToUiReleaseMode,
  getUiReleaseMode,
  () => resolveUiReleaseMode(import.meta.env.VITE_UI_RELEASE),
);

export const isRedesignRelease = (mode: UiReleaseMode = getUiReleaseMode()): boolean => (
  mode !== 'legacy'
);

export const shouldUseRedesignForSurface = (
  mode: UiReleaseMode,
  surface: UiReleaseSurface,
): boolean => UI_RELEASE_RANK[mode] >= UI_RELEASE_RANK[SURFACE_MINIMUM_STAGE[surface]];
